import { z } from 'zod/v3';

export const ApiCheckRequestDTOSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  provider: z.string(),
  operation: z.string(),
  durationMs: z.number(),
  request: z.record(z.unknown()),
  response: z.record(z.unknown()).optional(),
  error: z.record(z.unknown()).optional(),
});
export type ApiCheckRequestDTO = z.infer<typeof ApiCheckRequestDTOSchema>;

export const ApiCheckExecutionDTOSchema = z.object({
  id: z.string(),
  checkId: z.string(),
  connectionId: z.string(),
  sourceRunId: z.string().nullable(),
  sourceRunType: z.enum(['api_test', 'compliance_scan']).nullable(),
  initiatedBy: z.string().nullable(),
  executor: z.object({
    name: z.literal('Screenata native check engine'),
    version: z.string().nullable(),
    runtime: z.string(),
  }),
  sourceProvider: z.string(),
  startedAt: z.string(),
  finishedAt: z.string(),
  variables: z.record(z.unknown()),
  requests: z.array(ApiCheckRequestDTOSchema),
});
export type ApiCheckExecutionDTO = z.infer<typeof ApiCheckExecutionDTOSchema>;

/** Historical artifacts have no executions; malformed new artifacts fail loudly. */
export function readApiCheckExecutions(value: unknown): ApiCheckExecutionDTO[] | null {
  if (!value || typeof value !== 'object' || !('executions' in value) || value.executions == null) return null;
  return z.array(ApiCheckExecutionDTOSchema).parse(value.executions);
}

export const ApiTestRunPhaseDTOSchema = z.enum([
  'QUEUED',
  'RUNNING',
  'COMPLETED',
  'FAILED',
]);
export type ApiTestRunPhaseDTO = z.infer<typeof ApiTestRunPhaseDTOSchema>;

export const ApiTestRunResultStatusDTOSchema = z.enum([
  'PASS',
  'FAIL',
  'INCONCLUSIVE',
  'ERROR',
]);
export type ApiTestRunResultStatusDTO = z.infer<typeof ApiTestRunResultStatusDTOSchema>;

/** Current-scope assessment, separate from the immutable scanner verdict. */
export const ApiTestRunEvaluationDTOSchema = z.object({
  resultStatus: ApiTestRunResultStatusDTOSchema.nullable(),
  evaluatedAt: z.string(),
  noInScopeResources: z.boolean(),
  passed: z.number().int(),
  failed: z.number().int(),
  inconclusive: z.number().int(),
  excepted: z.number().int(),
  exceptionIds: z.array(z.string()),
});
export type ApiTestRunEvaluationDTO = z.infer<typeof ApiTestRunEvaluationDTOSchema>;

export const ApiTestRunActorDTOSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  image: z.string().nullable(),
  isServiceAccount: z.boolean(),
});
export type ApiTestRunActorDTO = z.infer<typeof ApiTestRunActorDTOSchema>;

export const ApiTestRunDTOSchema = z.object({
  id: z.string(),
  statusId: z.string(),
  workspaceId: z.string(),
  startedBy: z.string(),
  startedByEmail: z.string().nullable(),
  startedByUser: ApiTestRunActorDTOSchema.nullable(),
  provider: z.string(),
  tool: z.string(),
  command: z.string().nullable(),
  apiCheckKey: z.string().nullable(),
  phase: ApiTestRunPhaseDTOSchema,
  resultStatus: ApiTestRunResultStatusDTOSchema.nullable(),
  evaluation: ApiTestRunEvaluationDTOSchema,
  startedAt: z.date(),
  finishedAt: z.date().nullable(),
  durationMs: z.number().int().nullable(),
  summaryJson: z.unknown().nullable(),
  jsonArtifactUrl: z.string().nullable(),
  logArtifactUrl: z.string().nullable(),
  manifestUrl: z.string().nullable(),
  evidenceSubmissionId: z.string().nullable(),
  evidencePackId: z.string().nullable(),
  evidencePackStorageUrl: z.string().nullable(),
  errorMessage: z.string().nullable(),
  artifactErrorMessage: z.string().nullable(),
  metadata: z.unknown().nullable(),
});
export type ApiTestRunDTO = z.infer<typeof ApiTestRunDTOSchema>;
